import { clone } from 'lodash';
import { createLogger } from 'shared/services/logger';
import us from './defines';
import { Rectangle } from './lib/objects/';
import { NotificationService } from './lib/services/notification-service';
import { isNumber, stripLeadingAtSign } from './lib/utility/helpers';
import { setIconState, Toolbar } from './toolbar';
import ViewLayout from './viewLayout';
import ViewportAP from './viewportAP';
import {translate} from 'shared/services/I18nService';
import { renderSettings } from './render-settings';
const $t = translate.scope('uScope');

const logger = createLogger('UScope:UScopeViewer', ['log', 'warn', 'error']);

/*
 ________________________________________________________________________________________________
 This file contains the application uScope's primary object, also called self.
 Public functions in the uScope object are called by components within the uScope application
 and by those functions isolated in the file public-api.js which is the exposed interface to
 the application's parent.

 Object Name: UScopeViewer
 Overview: The uScope object creates and manages the layout of viewport divs and their
 associated viewport objects which manage the displayed images.

 Limitations:
 o Duplicate Image:
 The current design does NOT support the same image open more than once.
 If that is to change the following must be done.  (Many of these items are in-work or complete.)
 - the viewport div ids must include a unique id beyond the imageId, like the image index
 - the namespace of event handlers must also include something unique
 - find by id, and other such searches, need to be reviewed
 - compare index not id

 Data Structures:
 o images_ is an array containing references to valid, opened image viewports
 o rootRect_ is the uScope div position and dimensions in a Rectangle object

 HTML:
 The uScope parent div is created outside of uScope and its ID is passed into uScope in uScopeStart().
 uScope creates a viewLayout div and populates it with the viewport divs in buildVPDiv_().
 */
export default function UScopeViewer (eventChannel) {
  renderSettings.init();

  var initStatus = true;
  var self = this;

  // Mock annotations so it can standalone
  self.isRegionCopyActive = () => {};
  self.startCopyMoveRegion = () => {};
  self.stopCopyMoveRegion = () => {};

  // ..... to group fns in js parser, for ease of search (e.g. search for all 'USCOPE_INQUIRY')
  var USCOPE_EXTERNAL_INTERFACE = this; // public group of fns allowed to be called external to uScope (from file public-api.js)
  var USCOPE_IMAGE = this; // public group of fns related to open images
  var USCOPE_INQUIRY = this; // public group of inquiry fns
  var USCOPE_GET = this; // public group of get fns
  var USCOPE_OVERLAY = this; // public group of fns related to overlays

  // The following constants are injected by WebPack during the build process.
  var progDate = BUILD_DATE; // eslint-disable-line no-undef
  var progVersion = BUILD_VERSION; // eslint-disable-line no-undef

  USCOPE_GET.getProgVersion = function () {
    return progVersion;
  };
  USCOPE_GET.getProgDate = function () {
    return progDate;
  };

  // ............................................ construct_() local
  function construct_ () {
    self.events = eventChannel.$new();

    // HACK ALERT - extend uScope with conferencing methods. Don't want to break existing implementations.
    // These methods are provided in case the conferencing module is not included in the WV build or if
    // the user simply doesn't have rights to start a conference.
    // TODO: Refactor this out of uScope.
    self.isConfLeader = self.isModeConference = self.isConfMember = () => false;
  }

  // initialization vars .................................
  let userId; // user's id (database table 'users')
  let title; // start up title // not implemented
  USCOPE_GET.getUserId = function () {
    return userId;
  };

  let $uScopeDiv; // element cache to reduce the number of full-page DOM scans
  let rootRect_;

  // ............................................................................................
  // image/viewport container
  // ............................................................................................
  var viewLayout_; // object to implement jquery layout plugin

  var images_ = []; // array of image/cViewport objects in view
  var imageSelIdx_ = 0; // index of image with focus

  // toconsider: I'd like to remove this array - need to replace dependency on getfullurl and the like
  var imageInitState_ = []; // array of image initial state

  // ............................................ getImageCount() public
  // getImageCount - This function returns the open image count.
  USCOPE_IMAGE.getImageCount = function () {
    for (var i = 0, count = 0, len = images_.length; i < len; i++) {
      if (images_[i]) {
        count++;
      }
    }
    return count;
  };

  // ............................................................................................
  // image selection functions
  // ............................................................................................

  // ............................................ getThisImage() public
  //    getThisImage - This function returns a reference to the currently selected image or undefined.
  USCOPE_IMAGE.getThisImage = function () {
    return images_[imageSelIdx_];
  };

  // ............................................ getThisImageById() public
  //    getThisImageById - This function searches the images_ array for the passed imageId.
  //                It returns the image object or return -1 if the imageId is not in images_[].
  USCOPE_IMAGE.getThisImageById = function (imageId) { // imageId includes @ sign, e.g.'@2'
    var idx = findImageIdx_(imageId);
    return (idx < 0) ? undefined : images_[idx];
  };

  // ............................................ getSelImageIdx() public
  //    getSelImageIdx -  This function returns the currently selected image index.
  USCOPE_IMAGE.getSelImageIdx = function () {
    return imageSelIdx_;
  };

  USCOPE_IMAGE.getAllImages = function () {
    return images_;
  };

  // ............................................ setImageSelected() public
  //    setImageSelected -  This function selects the image related to the passed index.
  //    If that image is already selected, the selection is not made unless the optional force
  //    parameter is true.
  USCOPE_IMAGE.setImageSelected = function (idx, opt_bForce) {
    // note index is used b/c could have same image id open multiple places
    if (idx !== imageSelIdx_ || opt_bForce) {
      const state = imageInitState_[idx];
      if (!state) {
        logger.error('Attempted to select out of bounds viewport!');
        return;
      }
      imageSelIdx_ = idx;
      triggerUScopeEvent(us.evIMAGESELECTED, {
        imageId: state.imageId,
        uScopeIdx: imageSelIdx_
      });
      images_[idx].setImageSelected();
      self.events.trigger('uscope:viewport:selected', self.getThisImage());
    }
  };
  // ............................................ setImageNextSelected() public
  //    setImageNextSelected -  This function selects the next image in image list.
  // todo: it doesn't really do what it says it does... check out what is desired
  USCOPE_IMAGE.setImageNextSelected = function (idx) {
    if (imageSelIdx_ == idx) {
      imageSelIdx_ = -1;
      for (var i = 0; i < images_.length; i++) {
        if (images_[i] != undefined) {
          self.setImageSelected(i);
          return;
        }
      }
    }
  };
  // ............................................ isThisImageValid() public
  //    isThisImageValid - This function returns true if the currently selected image is valid and false otherwise.
  USCOPE_IMAGE.isThisImageValid = function () {
    return !!images_[imageSelIdx_];
  };

  //    isThisImageValid - This function returns true if the passed image ID is open and false otherwise.
  USCOPE_IMAGE.isThisImageIdValid = function (imageId) {
    for (var i = 0, len = images_.length; i < len; i++) {
      if (images_[i] != undefined) {
        var thisId = images_[i].getImageId();
        if (imageId == thisId) {
          return true;
        }
      }
    }
    return false;
  };

  // ............................................ findImageIdx_() local
  // findImageIdx_ - This function searches the images_ array for the passed imageId.
  //                It returns the index to the image with this imageId or return -1 if the imageId is not in images_[].
  function findImageIdx_ (imageId) {
    for (var i = 0; i < images_.length; i++) {
      if (images_[i] != undefined) {
        var aImageId = images_[i].getImageId();
        if (aImageId == imageId) {
          return i;
        }
      }
    }
    return -1;
  }

  // ............................................................................................
  // toolbar options from VIL
  // ............................................................................................
  // ............................................ toolbarCanAddButton() public
  USCOPE_INQUIRY.toolbarCanAddButton = function (tbOptionName) {
    if (self.vilSettings && self.vilSettings.toolbarOptions && self.vilSettings.toolbarOptions[tbOptionName]) {
      return self.vilSettings.toolbarOptions[tbOptionName];
    } else {
      return false;
    }
  };

  // ............................................................................................
  // mode related vars & functions
  // ............................................................................................
  var inLockDownMode = false; // flag indicating lock down mode
  // ............................................ isModeLockDown() public
  //    isModeLockDown - This function returns true if uScope is in lock down mode and false otherwise.
  USCOPE_INQUIRY.isModeLockDown = function () {
    return inLockDownMode;
  };

  // ............................................ setInLockDownMode() public
  //    setInLockDownMode - This function sets uScope into or out of lock down mode.
  //    In lock down mode drawing of annotations is disabled.
  //    TODO: If this is really only an annotations feature then make this part of the "annotations" API
  USCOPE_EXTERNAL_INTERFACE.setInLockDownMode = function (enable) { // switch into/out-of lock down mode
    if (enable && !inLockDownMode) {
      inLockDownMode = true;
      self.events.trigger('uScope:enter-lockdown');
    } else if (!enable && inLockDownMode) {
      inLockDownMode = false;
      self.events.trigger('uScope:exit-lockdown');
    }
  };

  // ............................................ canSaveAnnotations() public
  //    canSaveAnnotations - This function returns true if uScope can save annotations and false otherwise.
  USCOPE_INQUIRY.canSaveAnnotations = function () {
    return us.ENABLEAUTOANNOTSAVE;
  };
  // ............................................................................................
  // user rights related: TODO: Move to "user rights" store
  // ............................................................................................

  // canAnnotate - This function report if an annotation can be created.
  // Note that the 'saveAnnotations' option is a user right and means the user can view but not create annotations
  USCOPE_INQUIRY.canAnnotate = function () { // can create annotations?
    // TODO: refactor to allow uScope to be put into some kind of "read only" mode, decoupling the need for
    // TODO: annotations to know anything about conferencing, or vice-versa
    var thisImgCan = (imageSelIdx_ >= 0 && imageInitState_[imageSelIdx_]) // can the selected image create annots? (user rights)
      ? imageInitState_[imageSelIdx_].saveAnnotations : false;

    return (!self.isModeLockDown() && // lockdown or conferencing mode disables annot creation
            !self.isModeConference() &&
            thisImgCan &&
            us.ENABLEANNOTCREATE);
  };

  //    overlayRefresh - This function refreshes the overlays on all images.
  USCOPE_OVERLAY.overlayRefresh = function () {
    for (var i = 0; i < images_.length; i++) {
      var thisImg = images_[i];
      if (thisImg && thisImg.overLayout) {
        thisImg.overLayout.refresh();
      }
    }
  };

  // ............................................................................................
  // credentials functions
  // ............................................................................................
  // access to credentials ...............................
  // - this is necessary to initialize an image/cViewport so even though each image stores its own credentials
  // the uScope object maintains its own quick reference, unfortunately

  // toconsider: query the images themselves so imageInitState_ can be eliminated whe conferencing moves to VIL
  USCOPE_GET.getFullURL = function () {
    return (imageInitState_[imageSelIdx_]) ? imageInitState_[imageSelIdx_].fullURL : undefined;
  };
  USCOPE_GET.getServer = function () {
    return (imageInitState_[imageSelIdx_]) ? imageInitState_[imageSelIdx_].server : undefined;
  };

  // ............................................................................................
  // input settings management
  // ............................................................................................

  // ............................................ parseVILSettings() public
  // parseVILSettings() - This fuction parses the passed JSON formatted parameters...
  const parseVILSettings = function (settings) {
    self.vilSettings = clone(settings);
    var vilSettings = self.vilSettings;
    var status = true;

    us.SHOWALLTOOLBARICONS = false;
    us.DISABLEUSERPREFS2DB = false;

    // settings.setup ................................
    if (vilSettings.setup) {
      us.idROOT = vilSettings.setup.divId || us.idROOT;
      us.idHashROOT = '#' + us.idROOT;
      $uScopeDiv = $(us.idHashROOT);
      $uScopeDiv.addClass(us.clsROOTSTYLE);

      userId = Number(userId || vilSettings.setup.userid);
      title = title || vilSettings.setup.title;
    }
    if (userId === 0 || isNaN(userId)) {
      userId = -1; // invalidate user id - prevents calls depending upon it from executing
    }

    // settings.overlays .............................
    if (vilSettings.overlays.none) { // if disable all overlays
      us.ENABLELABEL =
                us.ENABLEMAG =
                    us.ENABLETHUMB =
                        us.ENABLESCALEBAR =
                            us.ENABLEZOOMCTL =
                                us.ENABLEHEATMAP =
                                    us.ENABLEROTATION = false;
      // note-I don't think this should disable the toolbar us.ENABLETOOLBAR =
    } else { // else enable/disable specific overlays
      us.ENABLELABEL = us.ENABLELABEL || vilSettings.overlays.label;
      us.ENABLEMAG = us.ENABLEMAG || vilSettings.overlays.magnifier;
      us.ENABLETHUMB = us.ENABLETHUMB || vilSettings.overlays.thumbnail;
      us.ENABLESCALEBAR = us.ENABLESCALEBAR || vilSettings.overlays.scalebar;
      us.ENABLEZOOMCTL = us.ENABLEZOOMCTL || vilSettings.overlays.zoombar;
      us.ENABLEROTATION = us.ENABLEROTATION || vilSettings.overlays.rotation;
      us.ENABLEHEATMAP = us.ENABLEHEATMAP || vilSettings.overlays.heatMap;
    }

    // settings.features .............................
    if (settings.features) {
      us.ENABLEAUTOANNOTSAVE = us.ENABLEAUTOANNOTSAVE || vilSettings.features.annotAutoSaveEnabled;
      us.ENABLELINKTOIMAGE = us.ENABLELINKTOIMAGE || vilSettings.features.linkToImage;
    }
    // settings.iapanels .............................
    if (settings.iapanels == undefined || settings.iapanels.panels == undefined ||
            settings.iapanels.panels.length == 0) { // if no iapanels are specified, disable the imageAnalysis button
      self.vilSettings.toolbarOptions['imageAnalysis'] = false;
    }

    return status;
  };
    // ............................................................................................
    // plugin and VIL interface helper functions
    // ............................................................................................

  // ............................................ getStateArray() public
  // getStateArray - This function returns json of the view state of the open image(s).
  USCOPE_EXTERNAL_INTERFACE.getStateArray = function (index) {
    var imgSettings = []; // array of imageJson
    var imageJson;

    // ..................................................
    if (index) {
      imageJson = imageStateGet(index);
      if (imageJson) {
        imgSettings.push(imageJson);
      }
    } else {
      for (var i = 0; i < images_.length; i++) {
        imageJson = imageStateGet(i);
        if (imageJson) {
          imgSettings.push(imageJson);
        }
      }
    }
    return imgSettings;

    // ..................................................
    // closure functions
    function imageStateGet (idx) {
      var thisImg = images_[idx];
      imageJson = undefined;
      if (thisImg) { // return send "normal" graphics coords
        imageJson = {
          'imagePath': thisImg.getImageId(),
          'fullURL': thisImg.getFullURL(),
          'x': thisImg.getViewCenterX(true),
          'y': thisImg.getViewCenterY(true),
          'z': thisImg.getViewFocus(),
          'mag': thisImg.getViewMag(),
          'angle': thisImg.getViewAngle(true),
          'sampleType': thisImg.sampleTypeGet(),
          'isPinned': thisImg.isPinned(),
          'saveAnnotations': imageInitState_[idx].saveAnnotations
        };
      }
      return imageJson;
    }

    // ..................................................
  };

  // ............................................................................................
  // image management
  // ............................................................................................

  // ............................................ imageUpdate() public
  //    imageUpdate - This function adds, replaces, removes or changes the state (properties) of one or more images.
  //
  //    usage:
  //        imageUpdate("add", jsonSettings);                        adds a new view of the image specified in jsonSettings
  //        imageUpdate("remove", jsonSettings);                    removes the view of the image specified in jsonSettings
  //        imageUpdate("replace", jsonOrig, jsonSettingsNew);    replaces jsonOrig image with jsonSettingsNew image
  //        imageUpdate("replace", jsonSettings);                    replaces the single unpinned image with the image specified in jsonSettings
  //        imageUpdate("state", jsonStateChange);                changes the state (pinned, sampleType) of the image in jsonSettings
  //
  //    where:
  //        jsonSettings and opt_jsonSettings are in image setting format
  //            (image or images subset of settings into uScopeStart())
  //
  //        jsonStateChange image setting format for sampleTyle change
  //            this will do:  { "imagePath": "@2", "sampleType": "positive", "isPinned": true}
  //                where only those states that changed need be indicated
  //
  USCOPE_EXTERNAL_INTERFACE.imageUpdate = function (action, jsonImageSet1, jsonImageSet2, error) {
    let status;
    var aImageState = parseImageSet_(jsonImageSet1); // parse the input settings into local arrays the formatted exactly like the by imageInitState_ array
    var bImageState = parseImageSet_(jsonImageSet2);

    // in PR DST replace action, sends imagePath of just '@' in a and valid in b //todo: remove this when DST is fixed
    if (aImageState.length == 0 && bImageState.length == 1) {
      aImageState = bImageState;
      action = 'add';
    }

    if (aImageState.length == 0) { // not a failure to have no images, just return
      return true;
    }

    var layoutInfo;
    if (action === 'add') { // dispatch by action
      if (hasDuplicates_(aImageState)) {
        return false;
      }
      logger.log('imageUpdate: add: ' + aImageState[0].imageId);
      // TODO: iterate for the N images
      layoutInfo = buildLayoutJSON_(aImageState);
      status = imageAdd(aImageState, layoutInfo);
    } else if (action === 'add-error') {
      logger.log('imageUpdate: error: ' + aImageState[0].imageId);
      status = imageAddError(aImageState, error);
    } else if (action === 'remove') {
      logger.log('imageUpdate: remove: ' + aImageState[0].imageId);
      // todo: iterate for the N images
      status = imageRemove(aImageState);
    } else if (action === 'replace') {
      // when there is NO DST and perhaps when there is no one tracking the currently open file
      // you can ask to replace the unpinned image without specifying it
      // Note: this is NOT normal operation with the DST - the DST must indicate which image is to be replaced to keep uScope's views in sync with the DST display
      if (bImageState.length == 0) { // if b is not passed in then replace the single unpinned image
        bImageState = aImageState; // b is (now, as usual) the image to be opened
        var idx = findUnPinnedImageIdx_(); // a is the image to be closed/replaced, an empty image array will cause an image add
        aImageState = (idx != -1) ? [{'imageId': images_[idx].getImageId()}] : [];
      }

      if (bImageState && aImageState[0].imageId == bImageState[0].imageId) { // don't replace an image with itself
        logger.log('imageUpdate: replace: ' + aImageState[0].imageId + ' already open');
        // TODO: iterate for the N images
        return true;
      }
      if (hasDuplicates_(bImageState) == true) {
        return false;
      }
      layoutInfo = buildLayoutJSON_(bImageState);
      logger.log('imageUpdate: replace: ' + aImageState[0].imageId + ' with ' + bImageState[0].imageId);
      status = imageReplace(aImageState, bImageState, layoutInfo); // todo: iterate for the N images
    } else if (action === 'state') {
      for (var a = 0; a < aImageState.length; a++) {
        logger.log('imageUpdate: ' + aImageState[a].imageId + ' to state: ' + aImageState[a].sampleType);
      }
      status = imageStateChange(aImageState);
    } else {
      logger.error('imageUpdate: unrecognized action: ' + action);
      return false;
    }
    triggerUScopeEvent(us.evIMAGEUPDATE, {}); // fire event: imageupdate completed
    return status;

    function findUnPinnedImageIdx_ () {
      for (var i = 0; i < images_.length; i++) {
        if (!images_[i] && !images_[i].isPinned()) {
          return i;
        }
      }
      return -1;
    }

    // parseVILImage_ - This function returns a json object of validated image initial settings, return false if not parsable
    function parseVILImage_ (aImage) {
      // initialize the return json object .............
      var bImage = {
        // begin official interface vars.........................................
        fullURL: aImage.fullURL, // e.g. /imageserver/@@token/@imageid2 or //host:82/@@token/@imageid

        // initial view settings: priority of view state:
        //        1st: regionId  2nd: xyz&mag 3rd: last selected region from annotations xml
        regionId: undefined,
        centerX: undefined,
        centerY: undefined, // center of view X,Y position in base image coords
        focus: undefined, // focus depth (z)
        mag: 'Fit', // magnfication value or "Fit", "Scan", "2", "4", ...
        zoom: undefined, // specified only when conferencing
        angle: 0,

        saveAnnotations: true, // default user rights to true ('save' really means 'create' annotations)
        title: undefined,
        sampleType: 'patient', // default sample type
        isPinned: false, // default pinned status
        // end official interface vars...........................................

        // derived vars .........................................................
        server: undefined, // e.g. /imageserver/  -  always via apache (not imageserver direct)
        token: undefined, // e.g. @@f5gHzmgSn6URMsJeIVbg==
        imageId: undefined // image identifier; e.g. @2 or imageserver/filename.ext
      };
      // parse image url ...............................
      if (bImage.fullURL) {
        // case 1: If you put a * in servers_list.php's External Host Names field, it will use the proxy rule, e.g./imageserver/@@token/@imageid
        // case 2: If you make the External Host Names field empty, it will talk to ImageServer directly, e.g. //host:82/@@token/@imageid
        // case 3: //host/redirect/@@token==/@id

        var thisURL = bImage.fullURL;
        bImage.server = thisURL.slice(0, thisURL.indexOf('@@')); // = /imageserver/
        bImage.server.toLowerCase(); // url is always lowercase but just be sure

        var tokenPlus = thisURL.slice(-(thisURL.length - thisURL.indexOf('@@'))); //  = @@f5gHzmgSn6URMsJeIVbg==/@2
        bImage.imageId = tokenPlus.slice(-(tokenPlus.length - tokenPlus.indexOf('/@') - 1)); // = @2
        bImage.token = tokenPlus.slice(0, tokenPlus.indexOf('/@')); // = @@f5gHzmgSn6URMsJeIVbg==
      } else if (aImage.imagePath && aImage.imagePath.indexOf('@') === 0) { // if fullURL is not given, this is usually uScope stand alone test harness driven (uname, pword given)
        var rawId = stripLeadingAtSign(aImage.imagePath);
        if (isNumber(rawId) == false) {
          logger.error('parseVILImage_: invalid imagePath: ' + aImage.imagePath);
          return;
        }

        bImage.imageId = aImage.imagePath;
        bImage.server = '/imageserver/';
        logger.log('parseVILImage_: fullURL missing, imagePath alone will be used.');
      } else { // here's where you'd look for a filepath but since that isn't handled yet
        return false;
      }

      // parse image initial state ...............................
      if (aImage.hasOwnProperty('x') && isNumber(aImage.x)) {
        bImage.centerX = Math.round(aImage.x);
      }
      if (aImage.hasOwnProperty('y') && isNumber(aImage.y)) {
        bImage.centerY = Math.round(aImage.y);
      }
      if (aImage.hasOwnProperty('z') && isNumber(aImage.z)) {
        bImage.focus = Math.round(aImage.z);
      }
      if (aImage.hasOwnProperty('mag') && (isNumber(aImage.mag) || aImage.mag === 'Fit' || aImage.mag === 'Scan')) { // mag can be "Fit", "Scan"
        bImage.mag = aImage.mag;
      }
      if (aImage.hasOwnProperty('zoom')) { // conferencing initial state specifies a zoom not a mag - conf is only time zoom is specified
        bImage.zoom = aImage.zoom;
      }
      if (aImage.hasOwnProperty('angle')) {
        bImage.angle = aImage.angle;
      }

      if (aImage.hasOwnProperty('SaveAnnotations')) { // input capital S from user rights
        bImage.saveAnnotations = aImage.SaveAnnotations;
      } else if (aImage.hasOwnProperty('saveAnnotations')) { // input small s from user rights
        bImage.saveAnnotations = aImage.saveAnnotations;
      }

      if (aImage.hasOwnProperty('title')) {
        bImage.title = aImage.title;
      }
      if (aImage.hasOwnProperty('sampleType')) {
        bImage.sampleType = aImage.sampleType;
      }
      if (aImage.hasOwnProperty('isPinned')) {
        bImage.isPinned = aImage.isPinned;
      }
      if (aImage.hasOwnProperty('regionId') && isNumber(aImage.regionId)) {
        bImage.regionId = aImage.regionId;
      }
      return bImage;
    }

    //    parseImageSet_ - This function parses the input json image data into the format used by imageInitState_ array.
    function parseImageSet_ (jsonImageSet) {
      var rtnArray = [];
      var imgState;
      if (jsonImageSet) {
        if (jsonImageSet.image) { // original single image format
          imgState = parseVILImage_(jsonImageSet.image);
          if (imgState != false) {
            rtnArray.push(imgState);
          }
        } else { // multi-image format
          for (var i = 0, len = jsonImageSet.length; i < len; i++) {
            imgState = parseVILImage_(jsonImageSet[i]); // separates server,token,imageid; sets undefined parms to defaults
            if (imgState != false) {
              rtnArray.push(imgState);
            } else {
              logger.error('imageUpdate: parseImageSet_: input image object could not be parsed');
            }
          }
        }
      }
      return rtnArray;
    }

    //    buildLayoutJSON_ - This function builds a list of information needed for ViewLayout to append an image.
    //        The information includes imageId, sampleType, vpclassid, and the outer viewport div, "jImgDiv"
    //        The outer viewport div is built here by calling buildVPDiv_() but is not sized.
    function buildLayoutJSON_ (aImageState) {
      var appendList = [];
      for (var i = 0; i < aImageState.length; i++) { // build the appendList that contains each image's key info nec. for viewLayout_ to manage it
        var vpclassid = us.classFromImageId(aImageState[i].imageId); // convert the imageId into a class name
        if (vpclassid) { // create the outer viewport div with the imageId as a class
          var jVpDivOuter = buildVPDiv_(vpclassid, false); //
          appendList.push({
            imageId: aImageState[i].imageId,
            sampleType: aImageState[i].sampleType,
            vpclassid: vpclassid,
            jImgDiv: jVpDivOuter
          });
        }
      }
      return appendList;
    }

    //    hasDuplicates_ - This function returns true if any image is duplicated in the input image list
    //                    or if any of those images are currently opened. otherwise it returns false;
    function hasDuplicates_ (aImageState) {
      var alen = aImageState.length;
      var i;
      for (i = 0; i < alen; i++) { // check if any image in aImageState is already open
        var imageIdx = findImageIdx_(aImageState[i].imageId);
        if (imageIdx != -1) { // if the imageId IS in the currently opened image list
          if (aImageState[i].server == images_[imageIdx].getServer()) { // if both the server name and the imageId match
            logger.error('imageUpdate: input image is already opened.');
            return true;
          }
        }
      }

      for (i = 0; i < alen; i++) { // check if any image in aImageState is duplicated in aImageState
        for (var j = i + 1; j < alen; j++) {
          if (aImageState[i].imageId == aImageState[j].imageId &&
                        aImageState[i].server == aImageState[j].server) {
            logger.error('imageUpdate: input image is duplicated.');
            return true;
          }
        }
      }
      return false;
    }
  };

  // ............................................ buildVPDiv_() local
  // buildVPDiv_ - This function creates the outer or inner viewport div and always returns the outer div.
  //                It does not size the div(s).
  // Notes:
  //        o create the VIEWPORT divs with the imageId as a class to uniquely identify them
  //        o vpclassid is an additional call on the viewport divs to differentiate each image's viewports
  //        o the outer vp div  is defined with the tabindex attribute so it can recieve key strokes
  function buildVPDiv_ (vpclassid, bBuildInner) {
    var jVpDivOuter;
    if (!bBuildInner) {
      jVpDivOuter = $('<div/>', {'class': us.clsOUTERVIEWPORT + ' ' + vpclassid});
      jVpDivOuter.attr({'tabindex': 0}); // for keystroke focus
    } else { // create the inner VP div
      jVpDivOuter = $('.' + us.clsOUTERVIEWPORT + '.' + vpclassid); // find the outer
      jVpDivOuter.append($('<div/>', {'class': us.clsVIEWPORT + ' ' + vpclassid, 'id': vpclassid}));// ol3
    }

    return jVpDivOuter;
  }

  // ............................................ imageAdd() public
  // This function adds images to the existing layout
  //        or if no images exist yet (and therefore no layout) it creates a layout and adds the images.
  //
  //    aImageState:    an array of initial states for the images....
  //    layoutInfo:        an array of specific information for the ViewLayout object
  //
  //    Note that this fn uses layout's append() to add images when none are displayed
  //        and expand() to add images when some are already present

  function imageAdd (aImageState, layoutInfo) {
    var curImageCount = self.getImageCount();
    if (curImageCount == us.multiSpecs.maxImages) {
      logger.error('Maximum number of images reached, will not add new image(s)');
      return;
    }

    var status = true;
    var openIdx = 0; // first image in imageInitState_ that needs to be opened

    if (viewLayout_ == undefined) { // ...................... create a new layout
      layoutInfo.length = Math.min(layoutInfo.length, us.multiSpecs.maxImages); // reduce num new images if necessary to enforce max image limit
      if (layoutInfo.length == 0) return false; // nothing to do

      rootRectCompute_();
      // construct the layout into the uScope div, size it
      viewLayout_ = new ViewLayout($uScopeDiv, rootRect_, layoutInfo.length, onResizePane_);
      status = viewLayout_.status; // check construction status
      if (status) {
        status = viewLayout_.append(layoutInfo); // arrange the images in panes and append their outer viewports to their panes
      }
      if (status) {
        imageInitState_ = aImageState; // initialize the image state array with this/these image[s]
        setIconState($('#' + us.idTBMULTIVIEW), us.idTBMULTIVIEW, viewLayout_.getPaneDesc());
      }
    } else { // ...................... change an existing layout
      layoutInfo.length = Math.min(layoutInfo.length, (us.multiSpecs.maxImages - curImageCount)); // reduce num new images if necessary to enforce max image limit
      if (layoutInfo.length == 0) return false; // nothing to do

      var newImageCount = curImageCount + layoutInfo.length;
      // construct the layout into the uScope div, size it
      var newviewLayout_ = new ViewLayout($uScopeDiv, rootRect_, newImageCount, onResizePane_);
      status = newviewLayout_.status; // check construction status
      if (status) {
        status = newviewLayout_.expand(viewLayout_, layoutInfo);
      }
      if (status) {
        viewLayout_ = newviewLayout_; // replace ref to viewLayout_ with new layout
        onResizePane_();

        openIdx = imageInitState_.length; // first image in the list that needs to be opened
        imageInitState_ = imageInitState_.concat(aImageState);

        setIconState($('#' + us.idTBMULTIVIEW), us.idTBMULTIVIEW, viewLayout_.getPaneDesc());
      }
    }

    if (status) { // for each new image: append the innner viewport and open the image into it
      for (var i = openIdx, len = imageInitState_.length; i < len; i++) {
        var vpclassid = layoutInfo[i - openIdx].vpclassid;
        buildVPDiv_(vpclassid, true);
        var imgStatus = createViewport(i); // open this image
        status = status && imgStatus;
      }
    }
    return status;
  }

  function imageAddError (aImageState) {
    // Note: This logic is incomplete. We should remove the image without triggering the opening of a new one.
    // Note: This function is (currently) only used when the viewport fails to load xInfo data from the server.
    if (!viewLayout_) {
      logger.error('imageAddError: no layout defined');
      return false;
    }

    aImageState.forEach(a => viewLayout_.remove(a.imageId));

    // It would be ideal if the error is evaluated before posting a notification but just shoring one now.
    const message = $t('loading-imageserver-error-message', {lineBreak: '<br>'});
    self.notifications.error(message, 0);
  }

  // ............................................ imageRemove()
  //    imageRemove - This function closes one or more open images.
  function imageRemove (aImageState) {
    if (!viewLayout_) {
      logger.error('imageRemove: no layout defined');
      return false;
    }

    var status;
    for (var a = 0, len = aImageState.length; a < len; a++) {
      var imageId = aImageState[a].imageId;
      imageClose1ById(imageId);
      // modify this viewLayout_ (change will not take immediate effect)
      viewLayout_.remove(imageId);
    }

    // now create a new layout of just the current images
    var newImageCount = self.getImageCount();
    if (newImageCount == 0) {
      viewLayout_ = undefined;
      logger.warn('imageRemove: no images open.');
      return true;
    }

    // construct the layout into the uScope div, size it
    var newviewLayout_ = new ViewLayout($uScopeDiv, rootRect_, newImageCount, onResizePane_);
    status = newviewLayout_.status; // check construction status
    if (status) {
      status = newviewLayout_.compress(viewLayout_); // move the viewport Divs from viewLayout_ into newviewLayout_
    }
    if (status) {
      viewLayout_ = newviewLayout_; // replace ref to viewLayout_ with new layout
      onResizePane_();
      setIconState($('#' + us.idTBMULTIVIEW), us.idTBMULTIVIEW, viewLayout_.getPaneDesc());
    }
    return status;
  }

  // ............................................ imageReplace()
  //    imageReplace - This function closes one image and opens another in its place.
  //    aImageState - image to be replaced
  //    bImageState - new image
  //    layoutInfo - of new image
  function imageReplace (aImageState, bImageState, layoutInfo) {
    if (!viewLayout_) {
      logger.log('uScope: imageReplace: no layout defined, calling imageAdd');
      return imageAdd(bImageState, layoutInfo);
    }
    if (aImageState.length !== bImageState.length) {
      logger.log('imageReplace: lopsided image arrays');
      return false;
    }

    self.events.trigger('uscope:image:replace');

    var status = true;
    for (var a = 0, len = aImageState.length; a < len; a++) {
      var imageId = aImageState[a].imageId;
      var imageIdx = imageClose1ById(imageId); // close image first
      if (imageIdx != -1) {
        imageInitState_[imageIdx] = bImageState[a]; // update the init state array with the replacement image
      }

      status = viewLayout_.replace(imageId, layoutInfo[a]); // update the layout with the replacement image
      if (status) {
        var vpclassid = layoutInfo[a].vpclassid;
        // NOTE (cc): Originally  buildVPDiv_ was assigned to an implicit global called jVpDivOuter. buildVpDiv()
        // does real work (it creates the required DOM structure for the next image's viewport), but jVpDivOuter
        // recalculated and used elsewhere.
        buildVPDiv_(vpclassid, true);
        createViewport(imageIdx); // open the replacement image
      }
    }
    return status;
  }

  // ............................................ imageArrange()
  // This function changes the arrangement of panes (aka layout) to the next in the sequence.
  USCOPE_IMAGE.imageArrange = function (reArrange) {
    var newPaneDesc = viewLayout_.getNextPaneDesc();
    if (newPaneDesc.length) {
      var curImageCount = self.getImageCount();
      // construct the layout into the uScope div, size it
      var newviewLayout_ = new ViewLayout($uScopeDiv, rootRect_, curImageCount, onResizePane_, newPaneDesc);
      var status = newviewLayout_.status; // check construction status
      if (status) {
        status = newviewLayout_.compress(viewLayout_); // arrange the images in panes and append their outer viewports to their panes
      }
      if (status) {
        viewLayout_ = newviewLayout_; // replace ref to viewLayout_ with new layout
        onResizePane_();

        setIconState($('#' + us.idTBMULTIVIEW), us.idTBMULTIVIEW, viewLayout_.getPaneDesc());
      } else { // todo: and for all other layout fns
      }
    }
  };
  // ............................................ imageStateChange()
  //    This function executes external image state changes.
  //    Image state changes handled are:
  //        sampleType:    when an opened image's sampleType is changed
  //
  function imageStateChange (aImageState) {
    if (!viewLayout_) {
      logger.warn('imageStateChange: no layout defined');
      return false;
    }

    var status = true;
    var bDoNewLayout = false;
    for (var a = 0, len = aImageState.length; a < len; a++) {
      var imageId = aImageState[a].imageId;
      var imageIdx = findImageIdx_(imageId); // find the image in images_
      if (imageIdx >= 0) {
        if (aImageState[a].sampleType && // sampleType change
                    aImageState[a].sampleType != imageInitState_[imageIdx].sampleType) {
          var sampleType = aImageState[a].sampleType;

          // todo: should I insure there are no other sample types of this kind?
          imageInitState_[imageIdx].sampleType = sampleType; // change the image sample type//cp-not sure this is nec, vp holds it
          images_[imageIdx].sampleTypeSet(sampleType); // tell the viewport of the sample type change

          // modify this viewLayout_ (change will not take immediate effect)
          viewLayout_.stateChange(aImageState[a].imageId, 'sampleType', sampleType);

          bDoNewLayout = true;
        }
        if (aImageState[a].isPinned && // isPinned change
                    aImageState[a].isPinned != imageInitState_[imageIdx].isPinned) {
          var isPinned = aImageState[a].isPinned;
          imageInitState_[imageIdx].isPinned = isPinned; // change the image pinned state//cp-not sure this is nec, vp holds it
          images_[imageIdx].isPinnedSet(isPinned); // tell the viewport of the pinned state change
        }
      }
    }

    if (bDoNewLayout) { // if a state change really was specified,
      // note that imageInitState_.length !=  self.getImageCount() b/c holes can be created in the array
      // construct the layout into the uScope div, size it
      var newviewLayout_ = new ViewLayout($uScopeDiv, rootRect_, self.getImageCount(), onResizePane_);
      status = newviewLayout_.status; // check construction status
      if (status) {
        status = newviewLayout_.compress(viewLayout_); // move the viewport Divs from viewLayout_ into newviewLayout_
      }

      if (status) {
        viewLayout_ = newviewLayout_; // replace ref to viewLayout_ with new layout
        onResizePane_();
        setIconState($('#' + us.idTBMULTIVIEW), us.idTBMULTIVIEW, viewLayout_.getPaneDesc());
      }
    }
    return status;
  }

  // ............................................ createViewport() local
  //    createViewport - This function opens the passed imageId by creating a viewport div,
  //                attaching them to the root node, creating a viewport object related to that div,
  //                and adding the new image to the image array.
  //
  //    Input parameters: imageInitState_[idx] contains the following - where idx is the image index -
  //        imageID:    required, image id number starting with the @ sign or filename starting with imageserver/
  //        rectDiv:    required, the position in the root div of this viewport
  //
  //        centerX:    optional, initial center of view X position
  //        centerY:    optional, initial center of view X position
  //        focus:        optional, initial focus (z position)
  //        mag:         optional, initial magnfiction
  //        regionId:    optional, initial region to center view on
  //        zoom:        optional, initial zoom
  //        angle:        optional, initial rotation angle in degrees
  //    added to the json:
  //        uScopeIdx:    required, the index into uScope's images_ array of this image
  //
  function createViewport (idx) {
    imageInitState_[idx].uScopeIdx = idx; // add the index into uScope's images_ array  of this image
    var initState = imageInitState_[idx]; // don't open if imageId isn't valid

    if (!initState.imageId || initState.imageId.length === 0 || !us.classFromImageId(initState.imageId)) {
      return false;
    }

    var imageId = initState.imageId;
    var vpclassid = us.classFromImageId(imageId); // convert the imageId into a class name
    if (!vpclassid) {
      // return error of the class name isn't valid
      return false;
    }

    var viewport = new ViewportAP(initState, self); // open the image into this viewport

    self.events.trigger('uscope:image:init', viewport);

    viewport.events.on('viewport:selected', USCOPE_IMAGE.setImageSelected, self);

    viewport.open();

    var openStatus = viewport.getStatus();
    if (openStatus) {
      images_[idx] = viewport; // add this image to image container
    }

    return openStatus;
  }

  // ............................................ closeAllButSelectedImage() public
  //    closeAllButSelectedImage - This function closes all open images except the selected image.
  USCOPE_IMAGE.closeAllButSelectedImage = function () {
    if (self.getImageCount > 1) {
      for (var i = 0; i < images_.length; i++) {
        if (i != imageSelIdx_ && images_[i] != undefined) {
          triggerUScopeEvent(us.evIMAGECLOSE, {imageId: images_[i].getImageId()});
        }
      }
    }
  };
  // ............................................ imageClose() public
  //    closeAllImages - This function closes all open images.
  function closeAllImages () {
    for (var i = 0; i < images_.length; i++) {
      imageClose1(i);
    }
    imageSelIdx_ = 0; // reset the current image ptr
    images_.splice(0); // clear the array - maybe not necessary?

    // toconsider: should the settings panel be deleted and protected from opening?
  }

  // ............................................ imageClose1ById() local
  //    imageClose1ById - This function closes the image with the passed imageId
  function imageClose1ById (imageId) {
    var imageIdx = findImageIdx_(imageId); // find the image in images_
    if (imageIdx >= 0) {
      imageClose1(imageIdx); // close this image
    } else {
      logger.warn('imageClose1ById: image is not open: ' + imageId);
    }
    return imageIdx;
  }

  // ............................................ imageClose1() local
  //    imageClose1 - This function closes the image at images_[imageIdx].
  function imageClose1 (imageIdx) {
    var thisImg = images_[imageIdx];

    if (thisImg) {
      var imageId = thisImg.getImageId();
      var vpclassid = us.classFromImageId(imageId); // convert the imageId into a class name

      thisImg.imageClose(); // close the image

      // faster to prevent memory leak and speed up DST switching images
      $('.' + us.clsVIEWPORT + '.' + vpclassid).remove();
      $('.' + us.clsOUTERVIEWPORT + '.' + vpclassid).detach().removeData();

      images_[imageIdx] = undefined;
      imageInitState_[imageIdx] = undefined;

      self.events.trigger('uscope:image:close', thisImg);
    }
  }

  // ............................................................................................
  // uscope root div managment
  // ............................................................................................

  // ............................................ rootRectCompute_() local
  //    rootRectCompute_ - This function calculates the current root div dimension and the root div desired
  //        position.  When not-in-development-mode the root div position must be 0,0 - relative to
  //        its normal position.
  function rootRectCompute_ () {
    var rootPos = $uScopeDiv.offset(); // position relative to the document
    rootRect_ = new Rectangle(rootPos.left, rootPos.top, $uScopeDiv.width(), $uScopeDiv.height());
  }

  // ............................................ rootRectGet() public
  USCOPE_GET.rootRectGet = function () {
    if (!rootRect_) {
      rootRectCompute_();
    }
    return rootRect_;
  };

  //    resize - This function sets the width and height of the root div.
  //        If not in-development-mode (e.g. not on its own page but given a div next to the DST)
  //            then there is a pr-esized 'idRoot' div for this function to return.
  //        If in-development-mode this function determines the maximum size possible to fill the
  //            window and sizes the 'idRoot' div accordingly.
  function resize () {
    var jRoot = $('#' + us.idROOT);
    if (jRoot.length === 0) { // make sure that the uscope div is defined
      logger.error('self.resize: uscope div is not defined.');
      return;
    } else if (jRoot.width() < 10) { // make sure that the uscope div is sized.
      logger.error('self.resize: uscope div is not sized.');
      return;
    }

    rootRectCompute_(); // insure rootRect_ is up to date
    if (viewLayout_) { // check b/c when opened w/o an image, no layout is defined
      viewLayout_.onResize(rootRect_); // update the layout panes
      onResizePane_(); // now the viewports inside the panes can be resized

      self.events.trigger('uscope:resize');
    }
    return true;
  };
  // ............................................ onResizePane_() local
  // onResizePane_ - This function is a callback when a pane is resized and called directly when the browser or DST resizes
  function onResizePane_ () {
    if (images_.length === 0) {
      return;
    }

    self.events.trigger('uScope:before-resize');

    rootRectCompute_(); // insure rootRect_ is up to date
    for (var i = 0; i < images_.length; i++) { // resize each viewport
      var thisImg = images_[i];
      if (thisImg) {
        thisImg.viewResize(false); // false means do not resize thumbnail
      }
    }

    self.events.trigger('uScope:after-resize');
  }

  // ............................................................................................
  // initialization
  // ............................................................................................

  // ............................................ reset() public
  // reset - This function resets to default values uScope vars that are initialized at uScopeStart.
  USCOPE_EXTERNAL_INTERFACE.reset = function () {
    userId = undefined;
    title = undefined;

    imageInitState_.splice(0); // clear the array
    images_.splice(0); // clear the array
    imageSelIdx_ = 0; //

    viewLayout_ = undefined;

    self.events.trigger('uScope:reset');
  };

  // ............................................ init - This function initializes self.
  // Note: because this fn sizes the uscope div, it is called by uScopeStart
  USCOPE_EXTERNAL_INTERFACE.init = function (settings) {
    // parse any passed settings including image ids and states
    if (settings) {
      initStatus = parseVILSettings(settings);
    }

    self.notifications = new NotificationService($uScopeDiv[0]);
    self.topToolbar = new Toolbar(self);

    // get uScope div size; sanity check it
    initStatus = initStatus && resize();

    if (!initStatus) {
      logger.error('init: Start up initialization failed');
    }

    return initStatus;
  };

  function unbindEvents () {
    const namespace = '.uscope';
    const $rootNode = $uScopeDiv;

    $rootNode.off(us.evREGIONSELECT + namespace);
    $rootNode.off(us.evLAYERSELECT + namespace);
    $rootNode.off(us.evMARKUPCHANGE + namespace);
    $rootNode.off(us.evREGIONDELETE + namespace);
  }

  USCOPE_EXTERNAL_INTERFACE.destroy = () => {
    closeAllImages();
    self.reset();
    self.events.$destroy();
    unbindEvents();
  };

  /* ****************************************************************************
  * BEGIN: This section was moved from public-api to eliminate uScope singleton
  **************************************************************************** */

  // ............................................ goToRegion() public
  //    goToRegion - This function positions the image at a region and selects it.
  //        The passed imageId must already be open.
  // TODO: VIL should do this itself
  self.goToRegion = function (imageId, regionId) {
    self.events.trigger(us.evREGIONSELECT, {
      imageId: imageId, // It doesn't look like imageId is used :/
      regionId: regionId,
      bReposition: true,
      bSaveSel: false
    });
    return true;
  };

  // ............................................ getImageState() public
  //    getImageState - This function returns the state of uScope's images.
  // TODO: Remove alias
  self.getImageState = self.getStateArray;

  // ............................................ resize() public
  // resize - This function reconfigures the viewer to fit within its root div.
  //    parameters:
  //        e : optional, event data. When called by the container, no event is passed.
  self.resize = function (e) {
    if (e) {
      const sTargetClass = $(e.target).first().attr('class');
      if (sTargetClass) { // because in FF, resize bubbles here first and then to resizables like the scaleBar
        return; // and that means that layout.refresh is called unnecessarily
      }
    }

    resize(); // update root div position and dims
  };

  // closeDialogs - This function closes all of uScope's dialogs.
  function closeDialogs () {
    // always verify this works with Use Case: HC change from one case to another and back again
    var jPanels = $uScopeDiv.find('.' + us.clsPANEL); // all uscope panels have this class
    jPanels.each(function () {
      // Note: MUST use === true below: if you call dialog("isOpen") on an element that is not yet a dialog,
      //    it returns an object. In IE8, this is interpreted as true
      if ($(this).dialog('isOpen') === true) {
        $(this).dialog('close');
      }
    });
    jPanels.remove(); // remove all panels from the DOM
  }

  // lockDown - This function sets the viewer's state into /out-of  lock down mode.
  self.lockDown = function (on) {
    self.setInLockDownMode(on);
  };

  //    stop - This function stops uScope by closing all images and all open dialogs.
  // todo: should probably remove the toolbar
  self.stop = function () {
    logger.log('uScopeStop called');
    closeDialogs();
    self.destroy();
  };

  /* ****************************************************************************
  * END
  **************************************************************************** */

  construct_();
}

// triggerUScopeEvent - This function fires the passed event with the passed parameters.
export function triggerUScopeEvent (evName, parms) {
  $(us.idHashROOT).trigger(evName, parms);
}



// WEBPACK FOOTER //
// ./src/uScope/js/_uScope.js